import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Popover,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
  Checkbox,
  FormControlLabel,
  InputAdornment,
  Tooltip,
} from "@mui/material";
import { Icon } from "@iconify/react";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import dayjs from "dayjs";
import { useDispatch } from "react-redux";
import { httpCommonAuth } from "../../app/api/axios/http.service";
import { setDialog } from "../../features/dialog/dialogSlice";

const STATUS_CONFIG = {
  submitted: { label: "Eingereicht", color: "warning" },
  confirmed: { label: "Bestätigt", color: "success" },
  waitlist: { label: "Warteliste", color: "default" },
  cancelled: { label: "Storniert", color: "error" },
  draft: { label: "Entwurf", color: "default" },
};

const PAYMENT_CONFIG = {
  unpaid: { label: "Offen", color: "error" },
  invoice_sent: { label: "Rechnung versendet", color: "warning" },
  sepa_exported: { label: "SEPA exportiert", color: "info" },
  paid: { label: "Bezahlt", color: "success" },
  refunded: { label: "Erstattet", color: "default" },
  not_applicable: { label: "Nicht fällig", color: "default" },
};

const SOURCE_LABELS = {
  member_portal: "Mitgliederportal",
  public: "Öffentliche Anmeldung",
  admin_manual: "Manuelle Buchung",
};

const BASE_FIELD_ORDER = [
  "participantFullName",
  "participantFirstName",
  "participantLastName",
  "participantBirthdate",
  "participantAge",
  "photoConsent",
  "registrationNumber",
  "registrationStatus",
  "paymentStatus",
  "bookingDate",
  "paymentMethod",
  "totalAmount",
  "bookerFullName",
  "bookerEmail",
  "bookerPhone",
  "bookerAddress",
  "pricingRule",
  "sessions",
  "source",
];

const DEFAULT_VISIBLE_FIELD_IDS = [
  "participantFullName",
  "participantBirthdate",
  "participantAge",
  "photoConsent",
  "bookerFullName",
  "bookerEmail",
  "bookerPhone",
  "registrationNumber",
  "registrationStatus",
  "paymentStatus",
  "bookingDate",
  "totalAmount",
  "sessions",
];

const formatCurrency = (value) =>
  new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency: "EUR",
  }).format(Number(value) || 0);

const formatDate = (value) => {
  if (!value) return "—";
  const parsed = dayjs(value);
  if (!parsed.isValid()) return "—";
  return parsed.format("DD.MM.YYYY");
};

const formatDateTime = (value) => {
  if (!value) return "—";
  const parsed = dayjs(value);
  if (!parsed.isValid()) return "—";
  return parsed.format("DD.MM.YYYY HH:mm");
};

const normalizeFieldValue = (value, type) => {
  if (value === null || typeof value === "undefined" || value === "") {
    return "—";
  }

  if (type === "checkbox") {
    return value === true ? "Ja" : "Nein";
  }

  if (type === "date") {
    return formatDate(value);
  }

  if (type === "number") {
    return Number.isFinite(Number(value)) ? String(value) : "—";
  }

  if (Array.isArray(value)) {
    return value.length > 0 ? value.join(", ") : "—";
  }

  if (typeof value === "object") {
    if (typeof value?.label === "string") return value.label;
    if (typeof value?.value === "string") return value.value;
    return Object.values(value)
      .filter((entry) => entry !== null && typeof entry !== "undefined" && entry !== "")
      .join(" · ");
  }

  return String(value);
};

const toComparableNumber = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : Number.NEGATIVE_INFINITY;
};

const toComparableDate = (value) => {
  if (!value) return Number.NEGATIVE_INFINITY;
  const timestamp = dayjs(value).valueOf();
  return Number.isFinite(timestamp) ? timestamp : Number.NEGATIVE_INFINITY;
};

const toSearchText = (value) => {
  if (value === null || typeof value === "undefined") return "";
  if (Array.isArray(value)) return value.join(" ");
  if (typeof value === "object") return Object.values(value).join(" ");
  return String(value);
};

const sanitizeFilename = (value = "") =>
  String(value || "event")
    .trim()
    .replace(/\s+/g, "_")
    .replace(/[^a-zA-Z0-9_-]/g, "") || "event";

const deriveAge = (birthdate) => {
  if (!birthdate) return null;
  const parsed = dayjs(birthdate);
  if (!parsed.isValid()) return null;
  const age = dayjs().diff(parsed, "year");
  return Number.isFinite(age) ? age : null;
};

export default function EventParticipantsOverviewTab({ event }) {
  const dispatch = useDispatch();
  const eventId = String(event?._id || "");
  const isMountedRef = useRef(true);

  const [registrations, setRegistrations] = useState([]);
  const [isLoadingRegistrations, setIsLoadingRegistrations] = useState(false);
  const [loadProgress, setLoadProgress] = useState({ page: 0, totalPages: 0 });

  const [searchInput, setSearchInput] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [paymentFilter, setPaymentFilter] = useState("ALL");
  const [photoFilter, setPhotoFilter] = useState("ALL");

  const [sortField, setSortField] = useState("participantLastName");
  const [sortOrder, setSortOrder] = useState("asc");

  const [fieldPickerAnchor, setFieldPickerAnchor] = useState(null);
  const [visibleFieldIds, setVisibleFieldIds] = useState(DEFAULT_VISIBLE_FIELD_IDS);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const eventSessionsById = useMemo(() => {
    const sessionMap = new Map();
    (event?.sessions || []).forEach((session) => {
      const sessionId = String(session?.sessionId || "").trim();
      if (!sessionId) return;
      sessionMap.set(sessionId, session?.label || sessionId);
    });
    return sessionMap;
  }, [event?.sessions]);

  const customFieldDefinitions = useMemo(() => {
    const rawFields = Array.isArray(event?.registrationConfig?.customFields)
      ? event.registrationConfig.customFields
      : [];

    return rawFields
      .map((field, index) => ({
        fieldId: String(field?.fieldId || "").trim(),
        label: String(field?.label || "").trim() || `Zusatzfeld ${index + 1}`,
        scope: field?.scope === "participant" ? "participant" : "booking",
        type: String(field?.type || "text").trim().toLowerCase(),
        sensitive: field?.sensitive === true,
      }))
      .filter((field) => Boolean(field.fieldId));
  }, [event?.registrationConfig?.customFields]);

  const fields = useMemo(() => {
    const base = [
      {
        id: "participantFullName",
        label: "Teilnehmer",
        group: "Teilnehmer",
        type: "text",
        align: "left",
      },
      {
        id: "participantFirstName",
        label: "Vorname",
        group: "Teilnehmer",
        type: "text",
        align: "left",
      },
      {
        id: "participantLastName",
        label: "Nachname",
        group: "Teilnehmer",
        type: "text",
        align: "left",
      },
      {
        id: "participantBirthdate",
        label: "Geburtsdatum",
        group: "Teilnehmer",
        type: "date",
        align: "left",
      },
      {
        id: "participantAge",
        label: "Alter",
        group: "Teilnehmer",
        type: "number",
        align: "right",
      },
      {
        id: "photoConsent",
        label: "Bildfreigabe",
        group: "Teilnehmer",
        type: "checkbox",
        align: "left",
      },
      {
        id: "registrationNumber",
        label: "Buchungsnummer",
        group: "Anmeldung",
        type: "text",
        align: "left",
      },
      {
        id: "registrationStatus",
        label: "Buchungsstatus",
        group: "Anmeldung",
        type: "text",
        align: "left",
      },
      {
        id: "paymentStatus",
        label: "Zahlungsstatus",
        group: "Anmeldung",
        type: "text",
        align: "left",
      },
      {
        id: "bookingDate",
        label: "Anmeldung am",
        group: "Anmeldung",
        type: "datetime",
        align: "left",
      },
      {
        id: "paymentMethod",
        label: "Zahlungsart",
        group: "Anmeldung",
        type: "text",
        align: "left",
      },
      {
        id: "totalAmount",
        label: "Betrag",
        group: "Anmeldung",
        type: "currency",
        align: "right",
      },
      {
        id: "bookerFullName",
        label: "Buchender",
        group: "Kontakt",
        type: "text",
        align: "left",
      },
      {
        id: "bookerEmail",
        label: "E-Mail",
        group: "Kontakt",
        type: "text",
        align: "left",
      },
      {
        id: "bookerPhone",
        label: "Telefon",
        group: "Kontakt",
        type: "text",
        align: "left",
      },
      {
        id: "bookerAddress",
        label: "Adresse",
        group: "Kontakt",
        type: "text",
        align: "left",
      },
      {
        id: "pricingRule",
        label: "Preisregel",
        group: "Anmeldung",
        type: "text",
        align: "left",
      },
      {
        id: "sessions",
        label: "Sessions",
        group: "Anmeldung",
        type: "text",
        align: "left",
      },
      {
        id: "source",
        label: "Quelle",
        group: "Anmeldung",
        type: "text",
        align: "left",
      },
    ];

    const custom = customFieldDefinitions.map((field) => ({
      id: `${field.scope}:${field.fieldId}`,
      label: `${field.scope === "participant" ? "Teilnehmer" : "Buchung"} · ${field.label}`,
      group: "Zusatzfelder",
      type: field.type,
      align: field.type === "number" ? "right" : "left",
      sensitive: field.sensitive === true,
    }));

    const baseById = new Map(base.map((entry) => [entry.id, entry]));
    const orderedBase = BASE_FIELD_ORDER.map((id) => baseById.get(id)).filter(Boolean);

    return [...orderedBase, ...custom];
  }, [customFieldDefinitions]);

  const fieldById = useMemo(
    () => new Map(fields.map((field) => [field.id, field])),
    [fields]
  );

  useEffect(() => {
    setVisibleFieldIds((current) => {
      const valid = current.filter((id) => fieldById.has(id));
      if (valid.length > 0) return valid;
      return DEFAULT_VISIBLE_FIELD_IDS.filter((id) => fieldById.has(id));
    });

    setSortField((current) => (fieldById.has(current) ? current : "participantLastName"));
  }, [fieldById]);

  const fetchAllRegistrations = useCallback(async () => {
    if (!eventId) {
      if (isMountedRef.current) setRegistrations([]);
      return;
    }

    if (isMountedRef.current) {
      setIsLoadingRegistrations(true);
      setLoadProgress({ page: 0, totalPages: 0 });
    }

    try {
      const perPage = 100;
      let pageNumber = 1;
      let totalPages = 1;
      const all = [];

      while (pageNumber <= totalPages) {
        const response = await httpCommonAuth.get(`/events/${eventId}/registrations`, {
          params: {
            page: pageNumber,
            limit: perPage,
            sort: "createdAt",
            order: "desc",
          },
        });

        const payload = response?.data || {};
        const pageRows = Array.isArray(payload.registrations) ? payload.registrations : [];
        all.push(...pageRows);

        totalPages = Math.max(1, Number(payload.totalPages) || 1);
        if (!isMountedRef.current) return;
        setLoadProgress({ page: pageNumber, totalPages });
        pageNumber += 1;
      }

      if (isMountedRef.current) setRegistrations(all);
    } catch (error) {
      if (isMountedRef.current) {
        dispatch(
          setDialog({
            type: "ERROR",
            title: "Teilnehmerübersicht konnte nicht geladen werden",
            data: {
              errorText:
                error?.response?.data?.message ||
                error?.data?.message ||
                "Bitte später erneut versuchen.",
            },
          })
        );
        setRegistrations([]);
      }
    } finally {
      if (isMountedRef.current) setIsLoadingRegistrations(false);
    }
  }, [dispatch, eventId]);

  useEffect(() => {
    fetchAllRegistrations();
  }, [fetchAllRegistrations]);

  const rows = useMemo(() => {
    const flattened = [];

    registrations.forEach((registration) => {
      const participants = Array.isArray(registration?.participants)
        ? registration.participants
        : [];

      const selectedSessionIds = Array.isArray(registration?.selectedSessionIds)
        ? registration.selectedSessionIds
        : [];
      const sessionLabels = selectedSessionIds
        .map((sessionId) => eventSessionsById.get(String(sessionId)) || String(sessionId))
        .join(", ");

      const booker = registration?.booker || {};
      const fullAddress = [
        booker?.street || "",
        [booker?.zip || "", booker?.city || ""].filter(Boolean).join(" "),
        booker?.country || "",
      ]
        .filter(Boolean)
        .join(", ");

      participants.forEach((participant, index) => {
        const participantName = [participant?.firstName || "", participant?.lastName || ""]
          .filter(Boolean)
          .join(" ")
          .trim();
        const bookerFullName = [booker?.firstName || "", booker?.lastName || ""]
          .filter(Boolean)
          .join(" ")
          .trim();

        const row = {
          rowId:
            participant?.participantId ||
            `${registration?._id || "reg"}-${index}-${participant?.firstName || ""}-${participant?.lastName || ""}`,
          participantFullName: participantName || "—",
          participantFirstName: participant?.firstName || "",
          participantLastName: participant?.lastName || "",
          participantBirthdate: participant?.birthdate || null,
          participantAge: deriveAge(participant?.birthdate),
          photoConsent: participant?.photoOptOut === true ? "Nein" : "Ja",
          photoConsentRaw: participant?.photoOptOut === true ? "no" : "yes",
          registrationNumber: registration?.registrationNumber || "",
          registrationStatus: registration?.registrationStatus || "",
          registrationStatusLabel:
            STATUS_CONFIG[registration?.registrationStatus]?.label ||
            registration?.registrationStatus ||
            "—",
          paymentStatus: registration?.paymentStatus || "",
          paymentStatusLabel:
            PAYMENT_CONFIG[registration?.paymentStatus]?.label ||
            registration?.paymentStatus ||
            "—",
          bookingDate: registration?.createdAt || null,
          paymentMethod: registration?.paymentMethod === "sepa"
            ? "SEPA"
            : registration?.paymentMethod === "bank_transfer"
              ? "Überweisung"
              : "—",
          totalAmount: Number(registration?.totalAmount) || 0,
          bookerFullName: bookerFullName || "—",
          bookerEmail: booker?.email || "",
          bookerPhone: booker?.phone || "",
          bookerAddress: fullAddress || "—",
          pricingRule: registration?.pricingSnapshot?.ruleLabelAtBooking || "—",
          sessions: sessionLabels || "—",
          source: SOURCE_LABELS[registration?.source] || registration?.source || "—",
          bookingCustomValues: registration?.customFieldValues || {},
          participantCustomValues: participant?.customFieldValues || {},
        };

        flattened.push(row);
      });
    });

    return flattened;
  }, [eventSessionsById, registrations]);

  const getFieldRawValue = useCallback((row, field) => {
    if (!field) return "";

    if (field.id.startsWith("booking:")) {
      const key = field.id.split(":")[1];
      return row?.bookingCustomValues?.[key];
    }

    if (field.id.startsWith("participant:")) {
      const key = field.id.split(":")[1];
      return row?.participantCustomValues?.[key];
    }

    return row?.[field.id];
  }, []);

  const getFieldDisplayValue = useCallback(
    (row, field) => {
      const raw = getFieldRawValue(row, field);

      if (field.id === "registrationStatus") {
        return row?.registrationStatusLabel || "—";
      }
      if (field.id === "paymentStatus") {
        return row?.paymentStatusLabel || "—";
      }
      if (field.type === "currency") {
        return formatCurrency(raw);
      }
      if (field.type === "datetime") {
        return formatDateTime(raw);
      }
      if (field.type === "date") {
        return formatDate(raw);
      }

      return normalizeFieldValue(raw, field.type);
    },
    [getFieldRawValue]
  );

  const filteredRows = useMemo(() => {
    const query = searchInput.trim().toLowerCase();

    return rows.filter((row) => {
      if (statusFilter !== "ALL" && row.registrationStatus !== statusFilter) {
        return false;
      }

      if (paymentFilter !== "ALL" && row.paymentStatus !== paymentFilter) {
        return false;
      }

      if (photoFilter === "YES" && row.photoConsentRaw !== "yes") {
        return false;
      }

      if (photoFilter === "NO" && row.photoConsentRaw !== "no") {
        return false;
      }

      if (!query) return true;

      const searchBlob = [
        row.participantFullName,
        row.bookerFullName,
        row.bookerEmail,
        row.bookerPhone,
        row.registrationNumber,
        row.registrationStatusLabel,
        row.paymentStatusLabel,
        row.sessions,
        ...Object.values(row.bookingCustomValues || {}),
        ...Object.values(row.participantCustomValues || {}),
      ]
        .map((value) => toSearchText(value).toLowerCase())
        .join(" ");

      return searchBlob.includes(query);
    });
  }, [paymentFilter, photoFilter, rows, searchInput, statusFilter]);

  const sortedRows = useMemo(() => {
    const field = fieldById.get(sortField) || fieldById.get("participantLastName");
    const orderFactor = sortOrder === "asc" ? 1 : -1;

    return [...filteredRows].sort((a, b) => {
      const valueA = getFieldRawValue(a, field);
      const valueB = getFieldRawValue(b, field);

      let result = 0;

      if (field?.type === "number" || field?.type === "currency") {
        result = toComparableNumber(valueA) - toComparableNumber(valueB);
      } else if (field?.type === "date" || field?.type === "datetime") {
        result = toComparableDate(valueA) - toComparableDate(valueB);
      } else if (field?.type === "checkbox") {
        result =
          (valueA === true || valueA === "Ja" ? 1 : 0) -
          (valueB === true || valueB === "Ja" ? 1 : 0);
      } else {
        result = String(valueA || "").localeCompare(String(valueB || ""), "de", {
          numeric: true,
          sensitivity: "base",
        });
      }

      if (result === 0) {
        return String(a.participantFullName || "").localeCompare(
          String(b.participantFullName || ""),
          "de",
          { numeric: true, sensitivity: "base" }
        );
      }

      return result * orderFactor;
    });
  }, [fieldById, filteredRows, getFieldRawValue, sortField, sortOrder]);

  const pagedRows = useMemo(() => {
    const start = page * rowsPerPage;
    return sortedRows.slice(start, start + rowsPerPage);
  }, [page, rowsPerPage, sortedRows]);

  const visibleFields = useMemo(
    () => visibleFieldIds.map((fieldId) => fieldById.get(fieldId)).filter(Boolean),
    [fieldById, visibleFieldIds]
  );

  const summary = useMemo(() => {
    const stats = {
      participants: sortedRows.length,
      uniqueBookings: new Set(sortedRows.map((row) => row.registrationNumber)).size,
      withPhotoConsent: sortedRows.filter((row) => row.photoConsentRaw === "yes").length,
      withoutPhotoConsent: sortedRows.filter((row) => row.photoConsentRaw === "no").length,
      paid: sortedRows.filter((row) => row.paymentStatus === "paid").length,
      unpaid: sortedRows.filter((row) => row.paymentStatus === "unpaid").length,
    };
    return stats;
  }, [sortedRows]);

  const toggleField = (fieldId) => {
    setVisibleFieldIds((current) => {
      if (current.includes(fieldId)) {
        if (current.length === 1) return current;
        return current.filter((id) => id !== fieldId);
      }
      return [...current, fieldId];
    });
  };

  const applyFieldPreset = (preset) => {
    if (preset === "all") {
      setVisibleFieldIds(fields.map((field) => field.id));
      return;
    }

    if (preset === "compact") {
      const compact = DEFAULT_VISIBLE_FIELD_IDS.filter((id) => fieldById.has(id));
      setVisibleFieldIds(compact.length > 0 ? compact : [fields[0]?.id].filter(Boolean));
      return;
    }

    if (preset === "contact") {
      const contactFields = [
        "participantFullName",
        "participantBirthdate",
        "participantAge",
        "bookerFullName",
        "bookerEmail",
        "bookerPhone",
        "bookerAddress",
        "registrationNumber",
      ].filter((id) => fieldById.has(id));

      setVisibleFieldIds(contactFields.length > 0 ? contactFields : [fields[0]?.id].filter(Boolean));
    }
  };

  const handleColumnSortClick = (fieldId) => {
    if (sortField === fieldId) {
      setSortOrder((current) => (current === "asc" ? "desc" : "asc"));
      return;
    }

    setSortField(fieldId);
    setSortOrder("asc");
  };

  const handleExportPdf = () => {
    if (sortedRows.length === 0 || visibleFields.length === 0) {
      dispatch(
        setDialog({
          type: "ERROR",
          title: "PDF-Export nicht möglich",
          data: { errorText: "Es sind keine Daten für den Export vorhanden." },
        })
      );
      return;
    }

    const isLandscape = visibleFields.length > 8;
    const doc = new jsPDF({
      unit: "pt",
      format: "a4",
      orientation: isLandscape ? "landscape" : "portrait",
    });

    const eventTitle = event?.eventName || "Veranstaltung";
    const eventDate = event?.eventDateBegin ? formatDateTime(event.eventDateBegin) : "—";

    doc.setFontSize(15);
    doc.text(`Teilnehmerübersicht · ${eventTitle}`, 36, 38);
    doc.setFontSize(10);
    doc.text(`Datum: ${eventDate}`, 36, 56);
    doc.text(`Erstellt am: ${dayjs().format("DD.MM.YYYY HH:mm")}`, 36, 72);
    doc.text(
      `Teilnehmer: ${summary.participants} · Buchungen: ${summary.uniqueBookings} · Felder: ${visibleFields.length}`,
      36,
      88
    );

    const tableHead = [visibleFields.map((field) => field.label)];
    const tableBody = sortedRows.map((row) =>
      visibleFields.map((field) => getFieldDisplayValue(row, field))
    );

    autoTable(doc, {
      startY: 102,
      head: tableHead,
      body: tableBody,
      styles: {
        fontSize: 8,
        cellPadding: 3,
        valign: "middle",
      },
      headStyles: {
        fillColor: [19, 31, 46],
        textColor: [255, 255, 255],
        fontStyle: "bold",
      },
      alternateRowStyles: {
        fillColor: [246, 249, 252],
      },
      margin: { top: 32, right: 24, bottom: 28, left: 24 },
      didDrawPage: (context) => {
        const pageNumber = context.pageNumber;
        const pageCount = doc.internal.getNumberOfPages();
        doc.setFontSize(8);
        doc.text(
          `Seite ${pageNumber} / ${pageCount}`,
          doc.internal.pageSize.getWidth() - 70,
          doc.internal.pageSize.getHeight() - 10
        );
      },
    });

    const filename = `teilnehmeruebersicht-${sanitizeFilename(eventTitle)}-${dayjs().format("YYYYMMDD_HHmm")}.pdf`;
    doc.save(filename);
  };

  const fieldGroups = useMemo(() => {
    const grouped = {
      Teilnehmer: [],
      Kontakt: [],
      Anmeldung: [],
      Zusatzfelder: [],
    };

    fields.forEach((field) => {
      if (!grouped[field.group]) grouped[field.group] = [];
      grouped[field.group].push(field);
    });

    return grouped;
  }, [fields]);

  const fieldPickerOpen = Boolean(fieldPickerAnchor);

  return (
    <Box>
      <Stack spacing={2}>
        <Paper
          variant="outlined"
          sx={{
            p: 2,
            background:
              "linear-gradient(140deg, rgba(37,99,235,0.16) 0%, rgba(5,150,105,0.13) 50%, rgba(15,23,42,0.08) 100%)",
            borderColor: "rgba(30,41,59,0.25)",
          }}
        >
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1.5}
            justifyContent="space-between"
            alignItems={{ md: "center" }}
          >
            <Stack spacing={0.25}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Teilnehmerübersicht (Admin)
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                Alle Teilnehmer inklusive Kontaktdaten, Status und ausgefüllter Anmeldeinformationen.
              </Typography>
            </Stack>

            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
              <Chip
                color="primary"
                icon={<Icon icon="mdi:account-group" width={16} />}
                label={`${summary.participants} Teilnehmer`}
              />
              <Chip
                color="success"
                icon={<Icon icon="mdi:check-circle-outline" width={16} />}
                label={`${summary.withPhotoConsent} Bildfreigabe`}
              />
              <Chip
                color="warning"
                icon={<Icon icon="mdi:cash-clock" width={16} />}
                label={`${summary.unpaid} offen`}
              />
            </Stack>
          </Stack>

          <Divider sx={{ my: 1.5, borderColor: "rgba(30,41,59,0.15)" }} />

          <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
            <TextField
              size="small"
              placeholder="Suche nach Teilnehmer, Kontakt, Buchungsnummer, Zusatzfeldern …"
              value={searchInput}
              onChange={(e) => {
                setSearchInput(e.target.value);
                setPage(0);
              }}
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Icon icon="mdi:magnify" width={18} />
                  </InputAdornment>
                ),
              }}
            />

            <FormControl size="small" sx={{ minWidth: 170 }}>
              <InputLabel>Buchungsstatus</InputLabel>
              <Select
                label="Buchungsstatus"
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value="ALL">Alle</MenuItem>
                {Object.entries(STATUS_CONFIG).map(([value, config]) => (
                  <MenuItem key={value} value={value}>
                    {config.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 170 }}>
              <InputLabel>Zahlungsstatus</InputLabel>
              <Select
                label="Zahlungsstatus"
                value={paymentFilter}
                onChange={(e) => {
                  setPaymentFilter(e.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value="ALL">Alle</MenuItem>
                {Object.entries(PAYMENT_CONFIG).map(([value, config]) => (
                  <MenuItem key={value} value={value}>
                    {config.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 155 }}>
              <InputLabel>Bildfreigabe</InputLabel>
              <Select
                label="Bildfreigabe"
                value={photoFilter}
                onChange={(e) => {
                  setPhotoFilter(e.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value="ALL">Alle</MenuItem>
                <MenuItem value="YES">Ja</MenuItem>
                <MenuItem value="NO">Nein</MenuItem>
              </Select>
            </FormControl>
          </Stack>

          <Stack
            direction={{ xs: "column", lg: "row" }}
            spacing={1}
            justifyContent="space-between"
            sx={{ mt: 1.5 }}
          >
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
              <Button
                size="small"
                variant="outlined"
                startIcon={<Icon icon="mdi:table-column-plus-after" width={16} />}
                onClick={(e) => setFieldPickerAnchor(e.currentTarget)}
              >
                Felder ({visibleFields.length})
              </Button>

              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Sortieren nach</InputLabel>
                <Select
                  label="Sortieren nach"
                  value={sortField}
                  onChange={(e) => setSortField(e.target.value)}
                >
                  {fields.map((field) => (
                    <MenuItem key={field.id} value={field.id}>
                      {field.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Button
                size="small"
                variant="outlined"
                startIcon={
                  <Icon
                    icon={sortOrder === "asc" ? "mdi:sort-ascending" : "mdi:sort-descending"}
                    width={16}
                  />
                }
                onClick={() => setSortOrder((current) => (current === "asc" ? "desc" : "asc"))}
              >
                {sortOrder === "asc" ? "Aufsteigend" : "Absteigend"}
              </Button>
            </Stack>

            <Stack direction="row" spacing={1}>
              <Tooltip title="Anmeldungen neu laden">
                <span>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={fetchAllRegistrations}
                    disabled={isLoadingRegistrations}
                    startIcon={
                      isLoadingRegistrations ? (
                        <CircularProgress size={14} />
                      ) : (
                        <Icon icon="mdi:refresh" width={16} />
                      )
                    }
                  >
                    Neu laden
                  </Button>
                </span>
              </Tooltip>

              <Button
                size="small"
                variant="contained"
                color="secondary"
                onClick={handleExportPdf}
                startIcon={<Icon icon="mdi:file-pdf-box" width={16} />}
                disabled={sortedRows.length === 0 || visibleFields.length === 0}
              >
                Als PDF drucken
              </Button>
            </Stack>
          </Stack>

          <Stack
            direction="row"
            spacing={0.75}
            useFlexGap
            flexWrap="wrap"
            sx={{ mt: 1.5 }}
          >
            {visibleFields.map((field) => (
              <Chip
                key={field.id}
                size="small"
                variant="outlined"
                label={field.label}
                onDelete={() => toggleField(field.id)}
                deleteIcon={<Icon icon="mdi:close" width={14} />}
              />
            ))}
          </Stack>
        </Paper>

        <Paper variant="outlined" sx={{ overflow: "hidden" }}>
          {isLoadingRegistrations ? (
            <Box sx={{ py: 4, textAlign: "center" }}>
              <CircularProgress />
              <Typography variant="body2" sx={{ mt: 1, color: "text.secondary" }}>
                Teilnehmerdaten werden geladen
                {loadProgress.totalPages > 0
                  ? ` (${loadProgress.page}/${loadProgress.totalPages})`
                  : "…"}
              </Typography>
            </Box>
          ) : sortedRows.length === 0 ? (
            <Box sx={{ py: 4, px: 2 }}>
              <Alert severity="info">Keine Teilnehmer für die aktuelle Filterung gefunden.</Alert>
            </Box>
          ) : (
            <>
              <TableContainer sx={{ maxHeight: 560 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      {visibleFields.map((field) => (
                        <TableCell
                          key={field.id}
                          align={field.align || "left"}
                          sx={{
                            fontWeight: 700,
                            backgroundColor: "rgba(15, 23, 42, 0.90)",
                            color: "#fff",
                            whiteSpace: "nowrap",
                          }}
                        >
                          <TableSortLabel
                            active={sortField === field.id}
                            direction={sortField === field.id ? sortOrder : "asc"}
                            onClick={() => handleColumnSortClick(field.id)}
                            sx={{ color: "inherit", "& .MuiTableSortLabel-icon": { color: "#fff !important" } }}
                          >
                            {field.label}
                          </TableSortLabel>
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pagedRows.map((row, rowIndex) => (
                      <TableRow
                        key={row.rowId}
                        hover
                        sx={{
                          backgroundColor: rowIndex % 2 ? "rgba(2, 132, 199, 0.03)" : "transparent",
                        }}
                      >
                        {visibleFields.map((field) => {
                          const value = getFieldDisplayValue(row, field);

                          return (
                            <TableCell
                              key={`${row.rowId}:${field.id}`}
                              align={field.align || "left"}
                              sx={{ verticalAlign: "top", maxWidth: 280 }}
                            >
                              {field.id === "registrationStatus" ? (
                                <Chip
                                  size="small"
                                  label={value}
                                  color={STATUS_CONFIG[row.registrationStatus]?.color || "default"}
                                />
                              ) : field.id === "paymentStatus" ? (
                                <Chip
                                  size="small"
                                  label={value}
                                  color={PAYMENT_CONFIG[row.paymentStatus]?.color || "default"}
                                />
                              ) : field.id === "photoConsent" ? (
                                <Chip
                                  size="small"
                                  label={value === "Ja" ? "Freigabe" : "Keine Freigabe"}
                                  color={value === "Ja" ? "success" : "error"}
                                  variant={value === "Ja" ? "filled" : "outlined"}
                                />
                              ) : (
                                <Typography
                                  variant="body2"
                                  sx={{
                                    whiteSpace: "pre-wrap",
                                    wordBreak: "break-word",
                                    fontFamily:
                                      field.id === "registrationNumber" ? "monospace" : "inherit",
                                  }}
                                >
                                  {value}
                                </Typography>
                              )}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                component="div"
                count={sortedRows.length}
                page={page}
                onPageChange={(_, nextPage) => setPage(nextPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[10, 25, 50, 100]}
                labelRowsPerPage="Zeilen:"
                labelDisplayedRows={({ from, to, count }) => `${from}–${to} von ${count}`}
              />
            </>
          )}
        </Paper>
      </Stack>

      <Popover
        open={fieldPickerOpen}
        anchorEl={fieldPickerAnchor}
        onClose={() => setFieldPickerAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <Box sx={{ p: 2, width: 460, maxWidth: "100vw" }}>
          <Stack spacing={1.25}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Sichtbare Felder
            </Typography>

            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
              <Button size="small" variant="outlined" onClick={() => applyFieldPreset("compact")}>
                Empfohlen
              </Button>
              <Button size="small" variant="outlined" onClick={() => applyFieldPreset("contact")}>
                Kontakt
              </Button>
              <Button size="small" variant="outlined" onClick={() => applyFieldPreset("all")}>
                Alle
              </Button>
            </Stack>

            <Divider />

            {Object.entries(fieldGroups).map(([groupName, groupFields]) => {
              if (!groupFields.length) return null;

              return (
                <Box key={groupName}>
                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      mb: 0.5,
                      color: "text.secondary",
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: "0.03em",
                    }}
                  >
                    {groupName}
                  </Typography>

                  <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
                    {groupFields.map((field) => {
                      const checked = visibleFieldIds.includes(field.id);
                      return (
                        <FormControlLabel
                          key={field.id}
                          control={
                            <Checkbox
                              size="small"
                              checked={checked}
                              onChange={() => toggleField(field.id)}
                            />
                          }
                          label={
                            <Stack direction="row" spacing={0.5} alignItems="center">
                              <Typography variant="body2">{field.label}</Typography>
                              {field.sensitive ? (
                                <Tooltip title="Als sensibel markiertes Feld">
                                  <Icon icon="mdi:shield-alert-outline" width={14} />
                                </Tooltip>
                              ) : null}
                            </Stack>
                          }
                          sx={{
                            mr: 0,
                            ml: 0,
                            px: 0.75,
                            py: 0.25,
                            borderRadius: 1,
                            border: "1px solid",
                            borderColor: checked ? "primary.main" : "divider",
                            backgroundColor: checked ? "rgba(37,99,235,0.08)" : "transparent",
                          }}
                        />
                      );
                    })}
                  </Stack>
                </Box>
              );
            })}

            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              Ein Feld muss immer sichtbar bleiben.
            </Typography>
          </Stack>
        </Box>
      </Popover>
    </Box>
  );
}

EventParticipantsOverviewTab.propTypes = {
  event: PropTypes.object,
};

EventParticipantsOverviewTab.defaultProps = {
  event: null,
};
